import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { updateContactAPI } from '../../../api/contacts';
import { isUnsavedContact } from '../contactRecordFlags';

/**
 * ContactModificationConfirmDialog - Confirmation dialog for saving modified contact
 *
 * Shows when user edits a contact inline and submits the RMA form.
 * Asks: "Apply to this RMA only" or "Update saved contact"
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close handler
 * @param {Object} props.modifiedContact - Contact with modifications
 * @param {function} props.onConfirm - Handler: (applyToRmaOnly: boolean) => void
 */
const ContactModificationConfirmDialog = ({ open, onClose, modifiedContact, onConfirm }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [choice, setChoice] = useState('rma_only'); // 'rma_only' or 'update_saved'
  const [saving, setSaving] = useState(false);

  // Address-derived and synthetic picker entries have no saved Contact row —
  // their id is a synthetic key ('address-<pk>'), never a Contact pk, so
  // there is nothing to update and the option is not offered.
  const hasSavedContact = !!modifiedContact?.id && !isUnsavedContact(modifiedContact);

  const handleConfirm = async () => {
    const applyToRmaOnly = choice === 'rma_only' || !hasSavedContact;

    // If updating saved contact, make API call
    if (!applyToRmaOnly && modifiedContact) {
      setSaving(true);
      try {
        await updateContactAPI(modifiedContact.id, {
          name: modifiedContact.name,
          business_name: modifiedContact.business_name,
          email: modifiedContact.email,
          phone1: modifiedContact.phone1,
          address: modifiedContact.address,
          city: modifiedContact.city,
          state: modifiedContact.state,
          zipcode: modifiedContact.zipcode, // Backend uses 'zipcode', not 'zip'
          country: modifiedContact.country,
        });
      } catch (err) {
        console.error('Failed to update contact:', err);
        // Continue anyway - user can manually update later
      } finally {
        setSaving(false);
      }
    }

    onConfirm(applyToRmaOnly);
    onClose();
  };

  if (!modifiedContact) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('contacts.saveContactChanges')}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('contacts.contactWasModified')}
        </Typography>

        {/* Contact Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 'var(--radius-md)' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {modifiedContact.name}
          </Typography>
          {modifiedContact.business_name && (
            <Typography variant="body2" color="text.secondary">
              {modifiedContact.business_name}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {modifiedContact.email}
          </Typography>
          {modifiedContact.full_address_display && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {modifiedContact.full_address_display}
            </Typography>
          )}
        </Box>

        {/* Radio Choices */}
        <RadioGroup value={choice} onChange={e => setChoice(e.target.value)}>
          <FormControlLabel
            value="rma_only"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('contacts.applyToRmaOnly')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Keep original contact unchanged, use modified data only for this RMA
                </Typography>
              </Box>
            }
          />
          {hasSavedContact && (
            <FormControlLabel
              value="update_saved"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('contacts.updateSavedContact')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Update the saved contact with these changes (affects future RMAs)
                  </Typography>
                </Box>
              }
            />
          )}
        </RadioGroup>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          {t('contacts.cancel')}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : t('contacts.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactModificationConfirmDialog;
